import { chatConfig } from '../config/chatConfig';
import { getTokenCount } from '../helpers/chatHelper';
import React, {
  useState,
  useCallback,
  useRef,
  TextareaHTMLAttributes,
  ChangeEvent,
  useEffect,
  KeyboardEvent,
  useMemo,
} from 'react';
import { debounce } from 'lodash';
import { useToast } from '../contexts/ToastContext';
import { useWindow } from '../contexts/WindowContext';
import { useMode } from '../contexts/ModeContext';
import { ConvexMessage, MessageDocument, MessageImage } from '../types/message';
import { ImageFileRecord, TextFileRecord } from '../types/files';
import { useAuth } from '../contexts/AuthContext';
import { processImageFile, processTextFile } from './FileHandler/processors';
import { checkIfFileIsAccepted, checkIfFileIsDuplicate, getFileCategory } from './FileHandler/utils';
import { Mode } from '../types/mode';
import { Model } from '../types/model';
import { useUndoRedo } from './useUndoRedo';
import {
  joinQuotedMessage,
  splitQuotedMessage,
} from '../helpers/quotedMessage';

type EditSubmitHandler = (event: {
  preventDefault: () => void;
  editContent: string;
  editMessage: ConvexMessage;
  editMode?: Mode;
  editModel?: Model | null;
  editImages: MessageImage[];
  editTextAttachments: MessageDocument[];
}) => void;

function attachmentRecordsFor(message: ConvexMessage): {
  textFiles: TextFileRecord[];
  imageFiles: ImageFileRecord[];
} {
  const textFiles = (message.textAttachments || [])
    .filter((doc) => !!doc.url)
    .map((doc, index): TextFileRecord => ({
      name: doc.title,
      size: 0,
      id: `edit-doc-${message._id}-${index}`,
      content: doc.content ?? null,
      isLoaded: true,
      type: doc.type,
      url: doc.url,
    }));
  const imageFiles = (message.images || [])
    .filter((image) => !!image.url)
    .map((image, index): ImageFileRecord => ({
      name: image.title,
      size: 0,
      id: `edit-img-${message._id}-${index}`,
      isLoaded: true,
      type: image.media_type,
      url: image.url,
    }));
  return { textFiles, imageFiles };
}

/**
 * Custom hook that centralizes the edit functionality.
 *
 * Uses the same memoization patterns as useInputTextArea:
 * - Refs to avoid state in callback dependencies
 * - useUndoRedo hook for stable undo/redo callbacks
 * - Deferred cursor restore (only on undo/redo/programmatic changes)
 * - Debounced height adjustment and token checks
 * - Stable EditArea component identity (no unmount/remount)
 */
export function useEditHandler() {
  const { mode: contextMode } = useMode();
  const [editContent, setEditContent] = useState<string>('');
  const [editMode, setEditMode] = useState<Mode>(contextMode);
  const [editCurrentModel, setEditCurrentModel] = useState<Model | null>(null);
  const [needsCursorRestore, setNeedsCursorRestore] = useState(false);
  const [editTextFiles, setEditTextFiles] = useState<TextFileRecord[]>([]);
  const [editImageFiles, setEditImageFiles] = useState<ImageFileRecord[]>([]);
  const { windowWidth } = useWindow();
  const toast = useToast();
  const auth = useAuth();

  const editTextFilesRef = useRef(editTextFiles);
  editTextFilesRef.current = editTextFiles;
  const editImageFilesRef = useRef(editImageFiles);
  editImageFilesRef.current = editImageFiles;

  // Refs for stable callback access (avoids editContent in deps)
  const editContentRef = useRef<string>(editContent);
  editContentRef.current = editContent;

  const cursorPositionRef = useRef<number | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const handleSubmitRef = useRef<EditSubmitHandler | null>(null);
  const limitExceededRef = useRef(false);

  // Use the optimized useUndoRedo hook (stable callbacks, no state in deps)
  const {
    currentValue: undoRedoValue,
    recordChange,
    undo,
    redo,
    resetHistory: resetUndoRedoHistory,
    updateCurrentState: updateUndoRedoState,
  } = useUndoRedo<string>({ initialValue: '' });

  // Sync undo/redo state with editContent (cheap string === check)
  useEffect(() => {
    if (editContent !== undoRedoValue) {
      updateUndoRedoState(editContent, cursorPositionRef.current);
    }
  }, [editContent, updateUndoRedoState, undoRedoValue]);

  // Deferred cursor restore — only fires on undo/redo/programmatic changes, NOT every keystroke
  useEffect(() => {
    if (needsCursorRestore && editTextareaRef.current && cursorPositionRef.current !== null) {
      setTimeout(() => {
        if (editTextareaRef.current) {
          editTextareaRef.current.selectionStart = cursorPositionRef.current!;
          editTextareaRef.current.selectionEnd = cursorPositionRef.current!;
        }
        setNeedsCursorRestore(false);
      }, 0);
    }
  }, [needsCursorRestore]);

  const updateHandleSubmit = useCallback((newHandleSubmit: EditSubmitHandler) => {
    handleSubmitRef.current = newHandleSubmit;
  }, []);

  const prepareEditContent = useCallback((content: string, message?: ConvexMessage) => {
    editImageFilesRef.current.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    const records = message ? attachmentRecordsFor(message) : { textFiles: [], imageFiles: [] };
    setEditTextFiles(records.textFiles);
    setEditImageFiles(records.imageFiles);
    const { messageText } = splitQuotedMessage(content);
    setEditContent(messageText);
    editContentRef.current = messageText;
    resetUndoRedoHistory(messageText);
    cursorPositionRef.current = messageText.length;
    limitExceededRef.current = false;
  }, [resetUndoRedoHistory]);

  // Stable height adjustment (no deps on state)
  const adjustEditTextareaHeight = useCallback(() => {
    if (editTextareaRef.current) {
      editTextareaRef.current.style.height = 'auto';
      const newHeight = Math.min(editTextareaRef.current.scrollHeight + 10, 350);
      editTextareaRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  // Debounced height adjustment for input changes (avoids thrashing layout)
  const debouncedAdjustHeight = useMemo(
    () => debounce(adjustEditTextareaHeight, 100),
    [adjustEditTextareaHeight]
  );

  // Debounced token check — expensive operation deferred, not run on every keystroke
  const debouncedTokenCheck = useMemo(
    () => debounce((text: string) => {
      if (!windowWidth || (windowWidth < 768 && text.length < chatConfig.limits.tokens.MAX_MESSAGE_LENGTH * 0.8)) {
        return;
      }
      try {
        const tokenCount = getTokenCount(text);
        if (tokenCount > chatConfig.limits.tokens.MAX_TOKEN_COUNT) {
          toast(`You've reached the input limit!`, 'error');
        }
      } catch (error) {
        console.error('Error counting tokens:', error);
      }
    }, 500),
    [toast, windowWidth]
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedAdjustHeight.cancel();
      debouncedTokenCheck.cancel();
    };
  }, [debouncedAdjustHeight, debouncedTokenCheck]);

  // Submit — uses ref for editContent (no editContent in deps)
  /** Returns false while uploads are still running. */
  const handleEditSubmit = useCallback(
    (message: ConvexMessage): boolean => {
      const textFiles = editTextFilesRef.current;
      const imageFiles = editImageFilesRef.current;
      if ([...textFiles, ...imageFiles].some((f) => !f.isLoaded || !f.url)) {
        toast('Attachments are still uploading. Please wait...', 'info');
        return false;
      }

      const currentContent = editContentRef.current;
      const { quotedText } = splitQuotedMessage(message.content || '');
      // No transport suffix: the server rebuilds markers from the sent attachments.
      const updatedContent = joinQuotedMessage(quotedText, currentContent);
      const editTextAttachments: MessageDocument[] = textFiles.map((file) => ({
        title: file.name,
        url: file.url,
        type: file.type,
        ...(file.content && file.type !== 'pdf' ? { content: file.content } : {}),
      }));
      const editImages: MessageImage[] = imageFiles.map((file) => ({
        title: file.name,
        url: file.url,
        media_type: file.type,
      }));

      setTimeout(() => {
        handleSubmitRef.current?.({
          preventDefault: () => {},
          editContent: updatedContent,
          editMessage: message,
          editMode: editMode || undefined,
          editModel: editCurrentModel || undefined,
          editImages,
          editTextAttachments,
        });
      }, 0);
      return true;
    },
    [editMode, editCurrentModel, toast]
  );

  const removeEditAttachment = useCallback((id: string) => {
    setEditTextFiles((prev) => prev.filter((f) => f.id !== id));
    setEditImageFiles((prev) => {
      const previewUrl = prev.find((f) => f.id === id)?.previewUrl;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const addEditFiles = useCallback((files: File[]) => {
    if (!auth.user) {
      toast('Sign in to Upload Files', 'error');
      return;
    }
    const maxFiles = chatConfig.limits.files.MAX_FILES_PER_MESSAGE;
    const matches = (file: File) => (f: { name: string; size: number; isLoaded: boolean }) =>
      !f.isLoaded && f.name === file.name && f.size === file.size;
    const updateText = (file: File, updates: Partial<TextFileRecord>) =>
      setEditTextFiles((prev) => prev.map((f) => (matches(file)(f) ? { ...f, ...updates, isLoaded: true } : f)));
    const updateImage = (file: File, updates: Partial<ImageFileRecord>) =>
      setEditImageFiles((prev) => prev.map((f) => (matches(file)(f) ? { ...f, ...updates, isLoaded: true } : f)));
    const handleError = (record: TextFileRecord | ImageFileRecord, error: any, fallbackMsg?: string) => {
      removeEditAttachment(record.id);
      toast(fallbackMsg || `Error processing ${record.name}`, 'error');
      console.error(`Error processing file ${record.name}:`, error);
    };

    let slots = maxFiles - editTextFilesRef.current.length - editImageFilesRef.current.length;
    if (slots <= 0) {
      toast(`You can only attach up to ${maxFiles} files per message.`, 'error');
      return;
    }

    (async () => {
      for (const file of files) {
        if (slots <= 0) {
          toast(`Only the first ${maxFiles} files can be attached.`, 'error');
          break;
        }
        if (checkIfFileIsDuplicate(file, editTextFilesRef.current, editImageFilesRef.current)) {
          toast(`${file.name} already attached`, 'error');
          continue;
        }
        if (!checkIfFileIsAccepted(file)) {
          const tooBig = file.size / (1024 * 1024) > chatConfig.limits.files.MAX_FILE_SIZE_MB;
          toast(
            tooBig
              ? `${file.name} exceeds the maximum size of ${chatConfig.limits.files.MAX_FILE_SIZE_MB}MB`
              : `File type ${file.type} not supported`,
            'error'
          );
          continue;
        }
        const category = getFileCategory(file);
        if (!category) {
          toast(`File type ${file.type || 'unknown'} not supported`, 'error');
          continue;
        }
        slots -= 1;
        if (category === 'image/png' || category === 'image/jpeg' || category === 'image/jpg') {
          const record = await processImageFile(file, updateImage, handleError);
          if (record) setEditImageFiles((prev) => [...prev, record]);
        } else {
          const record = await processTextFile(file, updateText, handleError);
          if (record) setEditTextFiles((prev) => [...prev, record]);
        }
      }
    })();
  }, [auth.user, toast, removeEditAttachment]);

  const resetHistory = useCallback(() => {
    resetUndoRedoHistory('');
    setEditContent('');
    editContentRef.current = '';
    cursorPositionRef.current = null;
    limitExceededRef.current = false;
  }, [resetUndoRedoHistory]);

  // Undo — uses useUndoRedo (stable deps, no editContent)
  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      setEditContent(previousState.value);
      editContentRef.current = previousState.value;
      cursorPositionRef.current = previousState.cursorPosition;
      setNeedsCursorRestore(true);
      if (previousState.value.length <= chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
        limitExceededRef.current = false;
      }
      setTimeout(adjustEditTextareaHeight, 0);
    }
  }, [undo, adjustEditTextareaHeight]);

  // Redo — uses useUndoRedo (stable deps, no editContent)
  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      setEditContent(nextState.value);
      editContentRef.current = nextState.value;
      cursorPositionRef.current = nextState.cursorPosition;
      setNeedsCursorRestore(true);
      limitExceededRef.current = nextState.value.length > chatConfig.limits.tokens.MAX_MESSAGE_LENGTH;
      setTimeout(adjustEditTextareaHeight, 0);
    }
  }, [redo, adjustEditTextareaHeight]);

  // Character limit — cheap inline check (string length only), expensive token check deferred
  const handleCharacterLimit = useCallback((text: string): boolean => {
    if (text.length > chatConfig.limits.tokens.MAX_MESSAGE_LENGTH) {
      if (!limitExceededRef.current) {
        limitExceededRef.current = true;
        toast(`You've reached the input limit!`, 'error');
      }
      return true;
    }
    limitExceededRef.current = false;
    debouncedTokenCheck(text);
    return false;
  }, [toast, debouncedTokenCheck]);

  // KeyDown — stable (no editContent dep)
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleUndo();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleRedo();
      return;
    }
  }, [handleUndo, handleRedo]);

  // Input change — stable (uses refs + recordChange/updateUndoRedoState, no editContent dep)
  const handleEditInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newCursorPosition = e.target.selectionStart;
      const newValue = e.target.value;
      const isOverLimit = handleCharacterLimit(newValue);

      if (!isOverLimit) {
        recordChange(newValue, newCursorPosition);
        setEditContent(newValue);
        editContentRef.current = newValue;
        cursorPositionRef.current = newCursorPosition;
      } else {
        const cutValue = newValue.slice(0, chatConfig.limits.tokens.MAX_MESSAGE_LENGTH);
        const adjustedCursorPosition = Math.min(newCursorPosition, cutValue.length);
        updateUndoRedoState(cutValue, adjustedCursorPosition);
        setEditContent(cutValue);
        editContentRef.current = cutValue;
        cursorPositionRef.current = adjustedCursorPosition;
        setNeedsCursorRestore(true);
      }

      debouncedAdjustHeight();
    },
    [handleCharacterLimit, debouncedAdjustHeight, recordChange, updateUndoRedoState]
  );

  // --- Stable EditArea component ---
  // Store latest handlers/data in a ref that the stable component reads from.
  // This avoids recreating the component identity on every render.
  const editAreaDataRef = useRef({
    editContent,
    handleEditInputChange,
    handleKeyDown,
    adjustEditTextareaHeight,
    editTextareaRef,
    addEditFiles,
  });
  editAreaDataRef.current = {
    editContent,
    handleEditInputChange,
    handleKeyDown,
    adjustEditTextareaHeight,
    editTextareaRef,
    addEditFiles,
  };

  // Created ONCE via useState initializer — same component identity across all renders.
  // React will update (not unmount/remount) the textarea when the parent re-renders.
  // The component reads latest values from editAreaDataRef.current each render.
  const [EditArea] = useState(() => {
    return function EditArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
      const data = editAreaDataRef.current;
      return (
        <textarea
          {...props}
          ref={(el) => {
            data.editTextareaRef.current = el;
            if (el) data.adjustEditTextareaHeight();
          }}
          className="edit-input"
          value={data.editContent.split('<|endofinput|>')[0]}
          onChange={data.handleEditInputChange}
          onKeyDown={data.handleKeyDown}
          onInput={data.adjustEditTextareaHeight}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files || []);
            if (files.length === 0) return;
            e.preventDefault();
            editAreaDataRef.current.addEditFiles(files);
          }}
          autoFocus
          spellCheck={true}
        />
      );
    };
  });

  return {
    setEditContent,
    handleEditSubmit,
    editContent,
    editMode,
    setEditMode,
    editCurrentModel,
    setEditCurrentModel,
    prepareEditContent,
    updateHandleSubmit,
    adjustEditTextareaHeight,
    handleUndo,
    handleRedo,
    resetHistory,
    EditArea,
    editTextFiles,
    editImageFiles,
    addEditFiles,
    removeEditAttachment,
  };
}
